<script setup lang="ts">
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { App } from "@xlabmedical/medic-web-framework";
import { onKeyStroke } from "@vueuse/core";

import { useWebsocket } from "@/composables/WebRgWebsocketComposable";
import {
    WebRgWSChatEvent,
    WebRgWSJobStatusUpdatedEvent,
    WebRgWSProjectFileAddedEvent,
    WebRgWSProjectFileDeletedEvent,
    WebRgWSProjectFileUpdatedEvent,
    WebRgWSProjectUpdatedEvent,
    WebRgWSWeblinkOrderCreatedEvent,
    WebRgWSWeblinkOrderUpdatedEvent,
} from "@/util/WebRgWebsocketManager";
import { storeToRefs } from "pinia";
import { usePatientsStore } from "@/stores/PatientsStore";
import { FileElement, ProjectFileType } from "@xlabmedical/medic-api";

const router = useRouter();
const patientsStore = usePatientsStore();
const { patients, projectsModel } = storeToRefs(patientsStore);

onKeyStroke("1", (e) => {
    if (e.ctrlKey) {
        router.push("/patients");
    }
});

onMounted(async () => {
    await patientsStore.ensureVendorName();
});

type ProjectFileEvent = WebRgWSProjectFileAddedEvent | WebRgWSProjectFileUpdatedEvent;

const objectTypes = [ProjectFileType.STL, ProjectFileType.Document, ProjectFileType.Image];
const ignoredTypes = new Set([ProjectFileType.ProjectAttachmentPreview, "stl_preview", ProjectFileType.ProcessingData]);

const resolveMobileFile = (event: ProjectFileEvent, eventFile: FileElement) => {
    if (eventFile.type === ProjectFileType.Mobile) {
        return eventFile;
    }

    const files = event.data?.files ?? [];
    if (eventFile.name?.endsWith(".zip")) {
        const mobileName = `${eventFile.name}.mobile.json`;
        const exactMatch = files.find((file) => file.type === ProjectFileType.Mobile && file.name === mobileName);
        if (exactMatch) {
            return exactMatch;
        }
    }

    const mobileFiles = files.filter((file) => file.type === ProjectFileType.Mobile);
    if (mobileFiles.length === 1) {
        return mobileFiles[0];
    }

    return undefined;
};

useWebsocket({
    chatMessageEvent: async (event: WebRgWSChatEvent) => {
        await patientsStore.updatePatientNotifications(event);
        await patientsStore.updatePatientListNotifications();
    },
    orderCreatedEvent: async (event: WebRgWSWeblinkOrderCreatedEvent) => {
        const { originProjectReference } = event.data;

        const patient = patients.value.find((p) => p.patientName === originProjectReference);

        if (patient) {
            await patientsStore.loadPatientFullByIdSlim(Number(patient.patientId), true);

            const projectsToLoad = projectsModel.value.filter((p) => !p.imageBuffer && p.projectDirty === true && p.patientId === patient.patientId);

            await Promise.all(projectsToLoad.map((project) => patientsStore.loadProjectDataSlim(project.id), true));
        }
    },
    orderUpdatedEvent: async (event: WebRgWSWeblinkOrderUpdatedEvent) => {
        await patientsStore.updateSinglePatientOrder(event);
    },
    projectFileAddedEvent: async (event: WebRgWSProjectFileAddedEvent) => {
        if (!event.data) {
            console.warn("Invalid event data PROJECT_FILE_ADDED: ", event);
            return;
        }

        const { id } = event.data;

        const patient = patients.value.find((p) => p.patientId === id.toString());

        if (!patient) {
            await patientsStore.loadPatientsSlim();
        }

        const eventFile = event.data.files?.find((f) => f.id === event.projectFileId);

        if (!eventFile) {
            console.warn("Invalid event data PROJECT_FILE_ADDED (missing file): ", event);
            return;
        }

        if (ignoredTypes.has(eventFile.type)) {
            console.warn("Invalid event data PROJECT_FILE_ADDED: ", event);
            return;
        }

        if (objectTypes.includes(eventFile.type)) {
            await patientsStore.addObjectFromWs(eventFile, event);
        } else {
            const mobileFile = resolveMobileFile(event, eventFile);
            if (!mobileFile) {
                console.warn("Invalid event data PROJECT_FILE_ADDED (missing mobile file): ", event);
                return;
            }
            await patientsStore.addProjectFromWS(mobileFile, event);
        }
    },
    projectFileUpdatedEvent: async (event: WebRgWSProjectFileUpdatedEvent) => {
        if (!event.data) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED: ", event);
            return;
        }

        const eventFile = event.data.files?.find((f) => f.id === event.projectFileId);

        if (!eventFile) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED (missing file): ", event);
            return;
        }

        if (ignoredTypes.has(eventFile.type)) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED 1: ", event);
            return;
        }

        if (objectTypes.includes(eventFile.type)) {
            await patientsStore.addObjectFromWs(eventFile, event);
        } else {
            const mobileFile = resolveMobileFile(event, eventFile);
            if (!mobileFile) {
                console.warn("Invalid event data PROJECT_FILE_UPDATED (missing mobile file): ", event);
                return;
            }
            await patientsStore.addProjectFromWS(mobileFile, event);
        }
    },
    projectFileDeletedEvent: async (event: WebRgWSProjectFileDeletedEvent) => {
        const deletedId = Number(event.projectFileId);
        let index = projectsModel.value.findIndex((f) => Number(f.projectFileId) === deletedId || Number(f.id) === deletedId);
        if (index !== -1) {
            projectsModel.value.splice(index, 1);
            if (patientsStore.selectedPatientProjects?.length) {
                const selectedIndex = patientsStore.selectedPatientProjects.findIndex((f) => Number(f.projectFileId) === deletedId || Number(f.id) === deletedId);
                if (selectedIndex !== -1) {
                    patientsStore.selectedPatientProjects.splice(selectedIndex, 1);
                }
            }
            return;
        }

        const removeById = <T extends { id?: number; projectFileId?: number }>(arr: T[]) => {
            const next = arr.filter((f) => Number(f.id) !== deletedId && Number(f.projectFileId) !== deletedId);
            if (next.length !== arr.length) {
                arr.splice(0, arr.length, ...next);
            }
        };

        removeById(patientsStore.projectFileModel);
        removeById(patientsStore.attachmentsFileModel);
        removeById(patientsStore.selectedPatientFileModel);
    },
    projectUpdatedEvent: async (event: WebRgWSProjectUpdatedEvent) => {
        await patientsStore.updateSingleProject(event);
    },
    projectAddedEvent: async () => {
        if (patients.value.length === 0) {
            await patientsStore.loadPatientsSlim();
        }
    },
    unzippedProjectFilesUpdatedEvent: async () => {
        return;
    },
    jobStatusUpdatedEvent: async (event: WebRgWSJobStatusUpdatedEvent) => {
        const status = SegmentationStatus[event.data.status as keyof typeof SegmentationStatus] ?? SegmentationStatus.UNKNOWN;

        await patientsStore.updateProjectSegmentationStatus(event.data.projectFileUuid, status);
    },
});
</script>

<template>
    <div>
        <App />
    </div>
</template>

<style scoped></style>
